import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import useTenant from '../hooks/useTenant';

export const Login: React.FC = () => {
  const { login } = useAuth();
  const { tenantName, logoUrl } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const redirectPath = (location.state as any)?.from?.pathname || '/';

  useEffect(() => {
    if (localStorage.getItem('school_saas_token')) {
      navigate(redirectPath);
    }
  }, [navigate, redirectPath]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all credential fields.');
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      if (rememberMe) {
        localStorage.setItem('portal_remember_email', email);
      } else {
        localStorage.removeItem('portal_remember_email');
      }
      navigate(redirectPath, { replace: true });
    } catch (err: any) {
      setError(err.toString());
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const savedEmail = localStorage.getItem('portal_remember_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-xl p-8 transition-all hover:shadow-2xl">
        
        {/* Branding Header */}
        <div className="text-center mb-8">
          {logoUrl ? (
            <img src={logoUrl} alt={tenantName} className="w-16 h-16 mx-auto rounded-xl object-cover mb-4 shadow-sm border border-slate-100" />
          ) : (
            <div className="w-16 h-16 mx-auto rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-3xl mb-4 shadow-inner">🏫</div>
          )}
          <h1 className="text-page-header tracking-tight mb-2">{tenantName}</h1>
          <p className="text-helper">Continuous Assessment & Roster Portal</p>
        </div>

        {/* Action Error Banners */}
        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-helper text-center flex items-center justify-center gap-2">
            <span>⚠️</span> {error}
          </div>
        )}

        {/* Credential Forms */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <label htmlFor="email" className="text-form-label uppercase tracking-wider block">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teacher@school.edu"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-table-body focus:ring-2 focus:ring-indigo-500/20 focus:bg-white focus:border-indigo-600 outline-none transition-all"
              required
              disabled={submitting}
            />
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label htmlFor="pass" className="text-form-label uppercase tracking-wider block">
                Password
              </label>
              <Link to="/forgot-password" className="text-helper text-indigo-600 hover:text-indigo-800 font-semibold transition-colors">
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input
                id="pass"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-4 pr-12 py-3 text-table-body focus:ring-2 focus:ring-indigo-500/20 focus:bg-white focus:border-indigo-600 outline-none transition-all"
                required
                disabled={submitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-3 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center">
            <input
              id="remember"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4.5 w-4.5 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded transition-all cursor-pointer"
            />
            <label htmlFor="remember" className="ml-2.5 text-helper select-none cursor-pointer text-slate-600 font-medium">
              Remember my email address
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-4 rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-50 text-nav flex items-center justify-center gap-2 mt-2"
          >
            {submitting ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                <span>Authenticating...</span>
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Demo Credentials Footer */}
        <div className="mt-8 text-center text-helper text-slate-500 border-t border-slate-100 pt-6 space-y-1">
          <p>Demo account: <strong className="text-slate-700">john.manda@morenese.edu</strong></p>
          <p>Password: <strong className="text-slate-700">password123</strong></p>
        </div>

      </div>
    </div>
  );
};

export default Login;
